import type { ZodSchema } from "zod";
import { z } from "zod";
import type {
    IsoAndUnixTimestamp} from "../date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
} from "../date-time/iso-and-unix";
import { productIdZodSchema } from "../products/product-id";
import type { ProviderId} from "../providers/provider-id";
import { providerIdZodSchema } from "../providers/provider-id";
import type {
    ColorTheme} from "../stylable/color-theme/color-theme";
import {
    colorThemeFactory,
    colorThemeZodSchema,
} from "../stylable/color-theme/color-theme";
import type {
    Translatable} from "../translations/translation";
import {
    translatableZodSchema,
} from "../translations/translation";
import type {
    BrandingBackground} from "./branding-background";
import {
    brandingBackgroundZodSchema,
} from "./branding-background";
import type { BrandingDto } from "./branding-dto";
import type { BrandingId} from "./branding-id";
import { brandingIdZodSchema } from "./branding-id";

type Branding = {
    _id: BrandingId;
    _createdAt: IsoAndUnixTimestamp;
    _type: "BRANDING";
    providerId?: ProviderId;
    state: "DRAFT" | "PUBLISHED";
    name: Translatable;
    colorThemes: {
        dark?: never;
        light: ColorTheme;
    };
    /**
     * Overrides back links and continuation buttons to always \
     * send the user to the provider's selected storefront url. \
     * @type {string}
     */
    storefrontUrl?: string;

    background?: BrandingBackground;

    product?: {
        background: BrandingBackground;
        peripheralBackground: BrandingBackground;
        margin: [large: number, medium: number, small: number];
    } | null;
    receipt?: {
        background: BrandingBackground;
        peripheralBackground: BrandingBackground;
    } | null;
};
const brandingZodSchema: ZodSchema<Branding> = z.object({
    _id: brandingIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("BRANDING"),
    name: translatableZodSchema,
    providerId: providerIdZodSchema.optional(),
    state: z.enum(["DRAFT", "PUBLISHED"]),
    colorThemes: z.object({
        dark: z.never().optional(),
        light: colorThemeZodSchema,
    }),
    storefrontUrl: z.string().url().optional(),
    productId: productIdZodSchema.optional(),

    background: brandingBackgroundZodSchema.optional(),

    product: z
        .object({
            background: brandingBackgroundZodSchema,
            peripheralBackground: brandingBackgroundZodSchema,
            margin: z.tuple([z.number(), z.number(), z.number()]),
        })
        .nullish(),
    receipt: z
        .object({
            background: brandingBackgroundZodSchema,
            peripheralBackground: brandingBackgroundZodSchema,
        })
        .nullish(),
});

function brandingFactory(
    dto: BrandingDto,
    fallbackColorThemes: {
        light: ColorTheme;
        dark?: never;
    },
): Branding {
    const { colorThemes, _type, ...data } = dto;
    const branding: Branding = {
        ...data,
        _type: "BRANDING",
        colorThemes: {
            light: colorThemeFactory(
                colorThemes.lightColorTheme,
                fallbackColorThemes.light,
            ),
        },
    };
    return branding;
}

export { brandingFactory, brandingZodSchema };
export type { Branding };
